import cn from '@commenda/utils/cn';
import { cva } from 'class-variance-authority';
import { Avatar, AvatarProps } from '../Avatar';
import { Tooltip } from '../Tooltip';

type AvatarSize = NonNullable<AvatarProps['size']>;

type AvatarGroupProps = {
  size?: AvatarProps['size'];
  className?: string;
  avatars: AvatarProps[];
  limit?: number;
  overlap?: 'snug' | 'spaced';
};

// Overlap scales with the avatar size (Figma: 16px avatars overlap by -6px, ≈ 3/8 of
// their diameter). A flat margin would look cramped on small avatars and barely
// overlapped on large ones, so each size gets its own negative left margin.
const overlapBySize: Record<AvatarSize, string> = {
  '2xs': '-ml-1.5', // -6px (matches Figma)
  xs: '-ml-2', // -8px (matches Figma)
  sm: '-ml-2.5', // -10px
  md: '-ml-3', // -12px
  lg: '-ml-3.5', // -14px
  xl: '-ml-4', // -16px
  '2xl': '-ml-5', // -20px
};

const overflowChipVariants = cva(
  'inline-flex items-center justify-center shrink-0 rounded-full border-1/2 border-border-secondary bg-bg-secondary text-text-primary',
  {
    variants: {
      size: {
        '2xs': 'size-4 text-[8px] font-medium leading-normal',
        xs: 'size-6 text-[10px] font-medium leading-normal',
        sm: 'size-8 txt-small-medium',
        md: 'size-10 txt-medium-medium',
        lg: 'size-12 txt-large-medium',
        xl: 'size-14 txt-extra-large-medium',
        '2xl': 'size-16 display-xs-medium',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

const AvatarGroup = ({
  size,
  className,
  avatars,
  limit = 3,
  overlap = 'snug',
}: AvatarGroupProps) => {
  // Avatar defaults to 'md' when size is omitted; mirror that so the overlap matches.
  const offset = overlap === 'snug' ? overlapBySize[size ?? 'md'] : undefined;

  return (
    <div className={cn('flex items-center', className)}>
      {avatars.slice(0, limit).map((avatar, index) => (
        // The overlap margin lives on this wrapper, not on the Avatar's className:
        // a badged Avatar renders its own positioning wrapper, so a margin on the
        // inner avatar div would shift it relative to its badge instead of the row.
        <div
          key={avatar.id || index}
          className={cn('flex self-center', index !== 0 && offset)}
        >
          <Avatar {...avatar} size={size} />
        </div>
      ))}
      {avatars.length > limit &&
        (() => {
          const hidden = avatars.slice(limit);
          const hiddenNames = hidden
            .map((avatar) => avatar.tooltip?.content ?? avatar.name)
            .filter((name): name is string => Boolean(name));

          const chip = (
            <div className={overflowChipVariants({ size })}>
              +{hidden.length}
            </div>
          );

          return (
            <div className={cn('relative z-10 flex self-center', offset)}>
              {hiddenNames.length > 0 ? (
                <Tooltip content={hiddenNames.join(', ')}>{chip}</Tooltip>
              ) : (
                chip
              )}
            </div>
          );
        })()}
    </div>
  );
};

export default AvatarGroup;
